import React, { useState, useEffect } from 'react';
import { Search, User, Users, X, ChevronDown, MapPin, CheckCircle } from 'lucide-react';
import pessoasService from '../services/pessoasService';

// Componente para seleção múltipla de pessoas com filtro por extensão
const PessoaSelectorMultiple = ({ 
  selectedPessoas = [], 
  onSelectPessoas, 
  error,
  disabled = false,
  placeholder = "Selecione as pessoas...",
  showInfo = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [extensoes, setExtensoes] = useState([]);
  const [selectedExtensao, setSelectedExtensao] = useState('');
  const [pessoas, setPessoas] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showExtensaoDropdown, setShowExtensaoDropdown] = useState(false);
  const [extensaoSearchTerm, setExtensaoSearchTerm] = useState('');
  const [extensoesFiltradas, setExtensoesFiltradas] = useState([]);

  // Carregar extensões ao montar o componente
  useEffect(() => {
    const carregarExtensoes = async () => {
      try {
        const response = await pessoasService.listarExtensoes();
        if (response.success) {
          setExtensoes(response.data);
          setExtensoesFiltradas(response.data);
          console.log('Extensões carregadas:', response.data);
        }
      } catch (error) {
        console.error('Erro ao carregar extensões:', error);
      }
    };

    carregarExtensoes();
  }, []);

  // Filtrar extensões quando o termo de busca mudar
  useEffect(() => {
    if (!extensaoSearchTerm) {
      setExtensoesFiltradas(extensoes);
    } else {
      const filtradas = extensoes.filter(extensao =>
        extensao.nome.toLowerCase().includes(extensaoSearchTerm.toLowerCase())
      );
      setExtensoesFiltradas(filtradas);
    }
  }, [extensaoSearchTerm, extensoes]);

  // Buscar pessoas quando extensão ou termo de busca mudar
  useEffect(() => {
    const buscarPessoas = async () => {
      if (!selectedExtensao) {
        setPessoas([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await pessoasService.listarPessoasPorExtensao(selectedExtensao, {
          page: 1,
          limit: 100,
          nome: searchTerm
        });
        
        if (response.success) {
          setPessoas(response.data || []);
          console.log('Pessoas encontradas:', response.data.length);
        } else {
          setPessoas([]);
        }
      } catch (error) {
        console.error('Erro ao buscar pessoas:', error);
        setPessoas([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(buscarPessoas, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedExtensao, searchTerm]);

  // Toggle seleção de pessoa (não fecha o dropdown)
  const handleTogglePessoa = (e, pessoa) => {
    e.preventDefault();
    e.stopPropagation();
    
    const isSelected = selectedPessoas.some(p => (p.id || p.idPessoa) === (pessoa.id || pessoa.idPessoa));
    
    let newSelection;
    if (isSelected) {
      newSelection = selectedPessoas.filter(
        p => (p.id || p.idPessoa) !== (pessoa.id || pessoa.idPessoa)
      );
    } else {
      newSelection = [...selectedPessoas, pessoa];
    }
    
    onSelectPessoas(newSelection);
  };

  // Selecionar todas as pessoas visíveis
  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const todasSelecionadas = pessoas.every(pessoa => 
      selectedPessoas.some(p => (p.id || p.idPessoa) === (pessoa.id || pessoa.idPessoa))
    );

    let newSelection;
    if (todasSelecionadas) {
      const idsVisiveis = pessoas.map(p => p.id || p.idPessoa);
      newSelection = selectedPessoas.filter(
        p => !idsVisiveis.includes(p.id || p.idPessoa)
      );
    } else {
      const novasPessoas = pessoas.filter(
        pessoa => !selectedPessoas.some(p => (p.id || p.idPessoa) === (pessoa.id || pessoa.idPessoa))
      );
      newSelection = [...selectedPessoas, ...novasPessoas];
    }
    
    onSelectPessoas(newSelection);
  };

  // Limpar todas as seleções
  const handleClearAll = () => {
    onSelectPessoas([]);
  };

  // Remover pessoa específica da seleção
  const handleRemovePessoa = (pessoa) => {
    const newSelection = selectedPessoas.filter(
      p => (p.id || p.idPessoa) !== (pessoa.id || pessoa.idPessoa)
    );
    onSelectPessoas(newSelection);
  };

  // Selecionar extensão
  const handleSelectExtensao = (extensao) => {
    setSelectedExtensao(extensao.id);
    setShowExtensaoDropdown(false);
    setPessoas([]);
    setSearchTerm('');
    setExtensaoSearchTerm('');
  };

  const extensaoSelecionada = extensoes.find(ext => ext.id === selectedExtensao);
  const todasVisivelsSelecionadas = pessoas.length > 0 && pessoas.every(pessoa => 
    selectedPessoas.some(p => (p.id || p.idPessoa) === (pessoa.id || pessoa.idPessoa))
  );

  return (
    <div className="relative">
      <div className="space-y-4">
        {/* Seletor de Extensão */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Extensão *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowExtensaoDropdown(!showExtensaoDropdown)}
              disabled={disabled}
              className={`w-full px-4 py-3 text-left border rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-colors ${
                disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
              } ${error ? 'border-red-300' : 'border-gray-300'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                  <span className={extensaoSelecionada ? 'text-gray-900' : 'text-gray-500'}>
                    {extensaoSelecionada ? extensaoSelecionada.nome : 'Selecione uma extensão...'}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                  showExtensaoDropdown ? 'transform rotate-180' : ''
                }`} />
              </div>
            </button>

            {/* Dropdown de extensões */}
            {showExtensaoDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-w-full">
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Digite o nome da extensão..."
                      value={extensaoSearchTerm}
                      onChange={(e) => setExtensaoSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                </div>
                
                <div className="max-h-64 overflow-y-auto overscroll-contain">
                  {extensoesFiltradas.length === 0 ? (
                    <div className="px-4 py-4 text-base text-gray-500 text-center">
                      {extensaoSearchTerm ? 
                        `Nenhuma extensão encontrada para "${extensaoSearchTerm}"` : 
                        'Nenhuma extensão encontrada'
                      }
                    </div>
                  ) : (
                    extensoesFiltradas.map((extensao) => (
                      <button
                        key={extensao.id}
                        type="button"
                        onClick={() => handleSelectExtensao(extensao)}
                        className="w-full px-4 py-3.5 text-left hover:bg-blue-50 flex items-center transition-colors focus:outline-none focus:bg-blue-50 border-b border-gray-100 last:border-b-0"
                      >
                        <MapPin className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                        <span className="text-base text-gray-900 truncate">{extensao.nome}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lista de pessoas selecionadas */}
        {selectedPessoas.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center text-sm font-medium text-blue-900">
                <Users className="w-4 h-4 mr-2" />
                {selectedPessoas.length} pessoa(s) selecionada(s)
              </div>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Limpar tudo
              </button>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {selectedPessoas.map((pessoa) => (
                <div
                  key={pessoa.id || pessoa.idPessoa}
                  className="flex items-center justify-between bg-white p-2 rounded border border-blue-200"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{pessoa.nome}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePessoa(pessoa)}
                    className="ml-2 p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seletor de Pessoas */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pessoa *
          </label>
          <div
            className={`border rounded-lg transition-colors ${
              disabled ? 'bg-gray-100' : 'bg-white'
            } ${error ? 'border-red-300' : 'border-gray-300'} ${
              isOpen ? 'ring-2 ring-blue-200 border-blue-500' : ''
            }`}
          >
            <div className="relative">
              <div className="flex items-center px-4 py-3">
                <Search className="w-4 h-4 text-gray-400 mr-2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => selectedExtensao && setIsOpen(true)}
                  placeholder={selectedExtensao ? "Digite o nome para buscar..." : "Selecione uma extensão primeiro"}
                  disabled={disabled || !selectedExtensao}
                  className="flex-1 outline-none bg-transparent text-gray-900 placeholder-gray-500"
                />
                {searchLoading && (
                  <div className="ml-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Dropdown de resultados */}
            {isOpen && selectedExtensao && (
              <div className="absolute z-40 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                {/* Header com opção de selecionar todos e botão fechar */}
                {pessoas.length > 0 && (
                  <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {todasVisivelsSelecionadas ? 'Desmarcar todos' : 'Selecionar todos'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center px-2 py-1 hover:bg-gray-100 rounded"
                    >
                      Fechar
                    </button>
                  </div>
                )}
                
                <div className="max-h-96 overflow-y-auto overflow-x-hidden">
                  {pessoas.length === 0 ? (
                    <div className="px-4 py-4 text-base text-gray-500 text-center">
                      {searchLoading ? 'Buscando...' : 
                       searchTerm ? 'Nenhuma pessoa encontrada' : 'Digite para buscar pessoas'}
                    </div>
                  ) : (
                    pessoas.map((pessoa) => {
                      const isSelected = selectedPessoas.some(
                        p => (p.id || p.idPessoa) === (pessoa.id || pessoa.idPessoa)
                      );
                      
                      return (
                        <button
                          key={pessoa.id || pessoa.idPessoa}
                          type="button"
                          onClick={(e) => handleTogglePessoa(e, pessoa)}
                          className={`w-full px-4 py-4 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 focus:outline-none transition-colors ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              {isSelected ? (
                                <CheckCircle className="w-5 h-5 text-blue-600 mr-3" />
                              ) : (
                                <User className="w-5 h-5 text-gray-400 mr-3" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div className={`text-base font-medium truncate ${
                                  isSelected ? 'text-blue-900' : 'text-gray-900'
                                }`}>
                                  {pessoa.nome}
                                </div>
                                {pessoa.status && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    pessoa.status === 'Aguardando Liberação' || pessoa.status === 'Aguardando Liberacao' ? 'bg-orange-500 text-white shadow-lg ring-2 ring-orange-300 animate-pulse' :
                                    pessoa.status === 'Ativo' ? 'bg-green-100 text-green-800' :
                                    pessoa.status === 'Inativo' ? 'bg-red-100 text-red-800' :
                                    pessoa.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {pessoa.status}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 space-y-1">
                                <div>CPF: {pessoa.cpf}</div>
                                <div>E-mail: {pessoa.email || 'Não informado'}</div>
                              </div>
                              {pessoa.numeroMatriculaPrincipal && (
                                <div className="text-sm text-blue-600 mt-1.5 font-medium">
                                  Matrícula Principal: {pessoa.numeroMatriculaPrincipal}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}

          {/* Info sobre extensão selecionada */}
          {extensaoSelecionada && (
            <p className="mt-2 text-xs text-gray-500">
              Buscando pessoas da extensão: <span className="font-medium">{extensaoSelecionada.nome}</span>
            </p>
          )}
        </div>
      </div>

      {/* Overlay para fechar dropdown quando clicar fora */}
      {showExtensaoDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowExtensaoDropdown(false)}
        />
      )}
      {isOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default PessoaSelectorMultiple;
